import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const RISCO_COR = { alto: '#dc2626', medio: '#d97706', baixo: '#2563eb' };
const RISCO_BG  = { alto: '#fef2f2', medio: '#fffbeb', baixo: '#eff6ff' };
const RISCO_LABEL = { alto: 'Alto', medio: 'Médio', baixo: 'Baixo' };

const inp = { padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', background: '#fff', height: 26 };
// Compactos: sem rolagem lateral (pedido do Vagner 24/07)
const th = { padding: '4px 6px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.02em', background: '#0a1628', whiteSpace: 'nowrap' };
const td = { padding: '4px 6px', fontSize: 11.5, color: '#374151', verticalAlign: 'top', borderBottom: '1px solid #e8eaed' };
const tagClasse = { display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700, margin: '1px 2px' };

// Data separada da hora (hora opcional) — mesmo padrão das tarefas da Agenda
function parseDataHora(val) {
  if (!val) return { data: '', hora: '' };
  const [data, hora] = val.includes('T') ? val.split('T') : [val, ''];
  return { data, hora: hora ? hora.slice(0, 5) : '' };
}
function combinarDataHora(data, hora) {
  if (!data) return '';
  return hora ? `${data}T${hora}` : data;
}
function DataHoraInput({ value, onChange, inpStyle }) {
  const { data, hora } = parseDataHora(value);
  function handleHora(e) {
    let v = e.target.value.replace(/[^0-9:]/g, '');
    if (/^\d{4}$/.test(v)) v = v.slice(0, 2) + ':' + v.slice(2);
    onChange(combinarDataHora(data, v));
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input type="date" value={data}
        onChange={e => onChange(combinarDataHora(e.target.value, hora))}
        style={{ ...inpStyle, flex: 1 }} />
      <input type="text" value={hora} placeholder="HH:MM" maxLength={5}
        onChange={handleHora}
        style={{ ...inpStyle, width: 70, flex: 'none' }} />
    </div>
  );
}

export default function Oposicao() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [rpis, setRpis] = useState([]);
  const [rpiSel, setRpiSel] = useState('');
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [filtroRisco, setFiltroRisco] = useState('todos');
  const [filtroClasse, setFiltroClasse] = useState('');
  const [filtroSitCliente, setFiltroSitCliente] = useState('todas');
  const [busca, setBusca] = useState('');

  // 📅 Tarefa pré-preenchida "Gerar oposição" — só escolher data e hora
  const [tarefaOp, setTarefaOp] = useState(null); // conflito selecionado
  const [tarefaData, setTarefaData] = useState('');
  const [tarefaSub, setTarefaSub] = useState('demandas');
  const [tarefaSalvando, setTarefaSalvando] = useState(false);
  const SUBAGENDAS_OP = [['', '📥 Geral'], ['administrativo', '🗂️ Administrativo'], ['comercial', '💼 Comercial'], ['cliente', '👥 Cliente'], ['demandas', '⚖️ Demandas Processuais'], ['pessoal', '🏠 Pessoal']];
  const salvarTarefaOp = async () => {
    if (!parseDataHora(tarefaData).data) return alert('Escolha a data (a hora é opcional).');
    setTarefaSalvando(true);
    try {
      const c = tarefaOp;
      await fetch('/agenda', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          descricao: `GERAR OPOSIÇÃO: ${c.cliente_marca} × "${c.rpi_nome_marca || '(figurativa)'}" (proc. ${c.rpi_numero}, RPI ${rpiSel}) — prazo de 60 dias da publicação (art. 158 da LPI)`,
          data_agendada: tarefaData,
          destinado_a: localStorage.getItem('nomeUsuario') || '',
          cliente_id: c.cliente_id || '',
          cliente_nome: c.cliente_nome || '',
          urgente: true,
          subagenda: tarefaSub,
        }),
      });
      setTarefaOp(null); setTarefaData('');
    } catch (e) {
      alert('Erro ao criar a tarefa: ' + (e?.message || ''));
    } finally {
      setTarefaSalvando(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/marcas-rpi/stats`, { headers })
      .then(r => r.json())
      .then(d => {
        setRpis(d.rpis || []);
        if (d.rpis && d.rpis.length > 0) setRpiSel(d.rpis[0].numero);
      })
      .catch(() => {});
  }, []);

  async function analisar() {
    if (!rpiSel) return;
    setCarregando(true);
    setErro('');
    setResultado(null);
    try {
      const r = await fetch(`${API}/marcas-rpi/oposicao?rpi=${rpiSel}`, { headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'Erro na análise');
      setResultado(d);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  const conflitos = resultado?.conflitos || [];

  // Situações existentes nas marcas dos clientes desta análise (pro filtro)
  const situacoesCliente = [...new Set(conflitos.map(c => c.cliente_situacao).filter(Boolean))].sort();

  const filtrados = conflitos.filter(c => {
    if (filtroRisco !== 'todos' && c.risco !== filtroRisco) return false;
    if (filtroSitCliente !== 'todas' && (c.cliente_situacao || '') !== filtroSitCliente) return false;
    if (filtroClasse && !c.rpi_classes.includes(filtroClasse) && !c.cliente_classes.includes(filtroClasse)) return false;
    if (busca) {
      const q = busca.toLowerCase();
      if (!c.rpi_nome_marca?.toLowerCase().includes(q) && !c.cliente_marca?.toLowerCase().includes(q) && !c.rpi_titular?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const totalAlto   = conflitos.filter(c => c.risco === 'alto').length;
  const totalMedio  = conflitos.filter(c => c.risco === 'medio').length;
  const totalBaixo  = conflitos.filter(c => c.risco === 'baixo').length;

  return (
    <div style={{ maxWidth: 1100 }}>
      {tarefaOp && (
        <div onClick={() => !tarefaSalvando && setTarefaOp(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 430, width: '100%', padding: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: '0 0 8px' }}>📅 Tarefa: gerar oposição</h2>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 12.5, color: '#374151', marginBottom: 10 }}>
              GERAR OPOSIÇÃO: <b>{tarefaOp.cliente_marca}</b> × "{tarefaOp.rpi_nome_marca || '(figurativa)'}" (proc. {tarefaOp.rpi_numero}, RPI {rpiSel}) — prazo de 60 dias da publicação
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Data <span style={{ fontWeight: 400, color: '#9ca3af' }}>(hora opcional)</span></label>
            <div style={{ margin: '4px 0 12px' }}>
              <DataHoraInput value={tarefaData} onChange={setTarefaData}
                inpStyle={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Subagenda</label>
            <div style={{ margin: '4px 0 12px' }}>
              <select value={tarefaSub} onChange={e => setTarefaSub(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }}>
                {SUBAGENDAS_OP.map(([v, l]) => <option key={v || 'g'} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setTarefaOp(null)} disabled={tarefaSalvando}
                style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={salvarTarefaOp} disabled={tarefaSalvando}
                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {tarefaSalvando ? 'Salvando…' : 'Criar tarefa 🔴'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: '#0a1628' }}>Análise de Oposição</h1>
        <p style={{ color: '#6b7280', fontSize: 12, marginTop: 1 }}>
          Selecione uma RPI importada e cruze as novas marcas publicadas com a base de clientes para identificar possíveis conflitos.
        </p>
      </div>

      {/* Seleção de RPI */}
      <div style={{ background: '#fff', borderRadius: 4, border: '1px solid #c8d0da', padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>RPI a analisar</label>
          <select value={rpiSel} onChange={e => setRpiSel(e.target.value)} style={{ ...inp, width: '100%' }}>
            {rpis.length === 0 && <option value="">Nenhuma RPI importada</option>}
            {rpis.map(r => <option key={r.numero} value={r.numero}>RPI {r.numero} — {r.data}</option>)}
          </select>
        </div>
        <button onClick={analisar} disabled={!rpiSel || carregando}
          style={{ padding: '4px 16px', background: carregando ? '#9ca3af' : '#0a1628', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: carregando ? 'not-allowed' : 'pointer', height: 26 }}>
          {carregando ? 'Analisando...' : 'Analisar'}
        </button>
      </div>

      {erro && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '8px 12px', color: '#dc2626', marginBottom: 10, fontSize: 12 }}>{erro}</div>
      )}

      {resultado && (
        <>
          {/* Resumo */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
            {[
              { label: 'Marcas na RPI', valor: resultado.total_marcas_rpi, cor: '#0a1628' },
              { label: 'Marcas de clientes', valor: resultado.total_marcas_clientes, cor: '#0a1628' },
              { label: 'Conflitos', valor: resultado.total_conflitos, cor: '#0a1628' },
              { label: 'Risco alto', valor: totalAlto, cor: '#dc2626' },
              { label: 'Risco médio', valor: totalMedio, cor: '#d97706' },
              { label: 'Risco baixo', valor: totalBaixo, cor: '#2563eb' },
            ].map(({ label, valor, cor }) => (
              <div key={label} style={{ background: '#fff', borderRadius: 4, border: '1px solid #c8d0da', padding: '8px 12px' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: cor }}>{valor}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{label}</div>
              </div>
            ))}
          </div>

          {resultado.total_conflitos === 0 ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: 16, textAlign: 'center', color: '#166534', fontSize: 13 }}>
              Nenhum conflito encontrado com marcas da base de clientes nesta RPI.
            </div>
          ) : (
            <>
              {/* Filtros */}
              <div style={{ background: '#fff', borderRadius: 4, border: '1px solid #c8d0da', padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input placeholder="Buscar marca ou titular..." value={busca} onChange={e => setBusca(e.target.value)}
                  style={{ ...inp, flex: 1, minWidth: 160, width: 'auto' }} />
                <select value={filtroRisco} onChange={e => setFiltroRisco(e.target.value)} style={inp}>
                  <option value="todos">Todos os riscos</option>
                  <option value="alto">Alto</option>
                  <option value="medio">Médio</option>
                  <option value="baixo">Baixo</option>
                </select>
                <select value={filtroSitCliente} onChange={e => setFiltroSitCliente(e.target.value)} style={inp}
                  title="Situação da marca do cliente da Preserva (da ficha do processo)">
                  <option value="todas">Cliente: todas as situações</option>
                  {situacoesCliente.map(s => <option key={s} value={s}>Cliente: {s}</option>)}
                </select>
                <input placeholder="Classe NCL..." value={filtroClasse} onChange={e => setFiltroClasse(e.target.value.replace(/\D/g, ''))}
                  style={{ ...inp, width: 120 }} />
                {(busca || filtroRisco !== 'todos' || filtroClasse || filtroSitCliente !== 'todas') && (
                  <button onClick={() => { setBusca(''); setFiltroRisco('todos'); setFiltroClasse(''); setFiltroSitCliente('todas'); }}
                    style={{ padding: '3px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>Limpar</button>
                )}
                <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{filtrados.length} resultado(s)</span>
              </div>

              {/* Tabela */}
              <div style={{ background: '#fff', borderRadius: 4, border: '1px solid #c8d0da', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={th}>Risco</th>
                      <th style={th}>Marca nova (RPI {rpiSel})</th>
                      <th style={th}>Situação</th>
                      <th style={th}>Classes RPI</th>
                      <th style={th}>Titular / UF</th>
                      <th style={th}>Marca do cliente</th>
                      <th style={th}>Classes cliente</th>
                      <th style={th}>Similaridade</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtrados.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{ background: RISCO_BG[c.risco], color: RISCO_COR[c.risco], padding: '2px 8px', borderRadius: 10, fontWeight: 700, fontSize: 11, border: `1px solid ${RISCO_COR[c.risco]}33` }}>
                            {RISCO_LABEL[c.risco]}
                          </span>
                        </td>
                        <td style={{ ...td, fontWeight: 600, maxWidth: 160 }}>
                          <div>{c.rpi_nome_marca || '(Figurativa)'}</div>
                          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>Nº {c.rpi_numero}</div>
                          {c.rpi_despachos && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{c.rpi_despachos}</div>}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {c.rpi_situacao ? (
                            <span style={{
                              display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                              background: c.rpi_situacao === 'Ativa' ? '#dcfce7' : c.rpi_situacao === 'Em andamento' ? '#fffbeb' : c.rpi_situacao === 'Inativa' ? '#fee2e2' : '#f3f4f6',
                              color: c.rpi_situacao === 'Ativa' ? '#15803d' : c.rpi_situacao === 'Em andamento' ? '#b45309' : c.rpi_situacao === 'Inativa' ? '#991b1b' : '#374151',
                            }} title={c.rpi_situacao === 'Em andamento' ? 'Pedido em exame — a oposição cabe agora, no prazo de 60 dias da publicação' : ''}>
                              {c.rpi_situacao}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {c.rpi_classes.length > 0 ? c.rpi_classes.map(cl => <span key={cl} style={tagClasse}>{cl}</span>) : '—'}
                        </td>
                        <td style={{ ...td, maxWidth: 140 }}>
                          <div style={{ fontWeight: 500 }}>{c.rpi_titular || '—'}</div>
                          {c.rpi_uf && <div style={{ fontSize: 10, color: '#9ca3af' }}>{c.rpi_uf}</div>}
                        </td>
                        <td style={{ ...td, fontWeight: 600, color: '#0a1628', maxWidth: 140 }}>
                          {c.cliente_marca}
                          {c.cliente_situacao && (
                            <span style={{
                              display: 'inline-block', marginLeft: 5, padding: '0 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'middle',
                              background: c.cliente_situacao === 'Ativa' ? '#dcfce7' : c.cliente_situacao === 'Em andamento' ? '#fffbeb' : c.cliente_situacao === 'Inativa' ? '#fee2e2' : '#f3f4f6',
                              color: c.cliente_situacao === 'Ativa' ? '#15803d' : c.cliente_situacao === 'Em andamento' ? '#b45309' : c.cliente_situacao === 'Inativa' ? '#991b1b' : '#374151',
                            }}>{c.cliente_situacao}</span>
                          )}
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 400, marginTop: 1 }}>{c.cliente_nome}</div>
                          {c.cliente_processo && <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace', marginTop: 1 }}>Nº {c.cliente_processo}</div>}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {c.cliente_classes.length > 0 ? c.cliente_classes.map(cl => (
                            <span key={cl} style={{ ...tagClasse, background: '#f0fdf4', color: '#166534', borderColor: '#86efac' }}>{cl}</span>
                          )) : '—'}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                            <div style={{ flex: 1, height: 5, background: '#e5e7eb', borderRadius: 3, minWidth: 32 }}>
                              <div style={{ height: 5, background: RISCO_COR[c.risco], borderRadius: 3, width: `${c.similaridade}%` }} />
                            </div>
                            <span style={{ fontWeight: 700, color: RISCO_COR[c.risco], minWidth: 28, fontSize: 11 }}>{c.similaridade}%</span>
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => setTarefaOp(c)} title="Criar tarefa na Agenda já preenchida com esta oposição — só escolher data e hora"
                            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            📅 Tarefa
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtrados.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                    Nenhum conflito encontrado com os filtros aplicados.
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
